import { z } from 'zod';
import { DocId, IsoTimestamp, TenantId } from '../common.js';
export const NieuwsPostType = z.enum(['post', 'link']);
export const NieuwsPostStatus = z.enum(['draft', 'published']);
export const NieuwsPostSchema = z.object({
    id: DocId,
    tenantId: TenantId,
    type: NieuwsPostType,
    title: z.string().min(1).max(200),
    excerpt: z.string().max(500).optional(),
    content: z.string().max(50000).optional(),
    imageUrl: z.string().url().optional(),
    // Fotoalbum. Alleen de detailweergave (`GET /:id`) levert `photos` uit: elke
    // foto kost een aparte sign-call, dus een lijst van 50 berichten × 64 foto's
    // zou 3200 calls per pageload zijn. De lijst krijgt alleen `photoCount`.
    photos: z.array(z.string().url()).optional(),
    // Miniaturen (~400 px) voor het raster, zelfde volgorde als `photos`.
    photoThumbs: z.array(z.string().url()).optional(),
    photoCount: z.number().int().min(0).optional(),
    // Onbewerkte opslagverwijzingen — alleen op de detailweergave, voor het
    // bewerkscherm. `imageUrl`/`photos` zijn ondertekende URL's die na een uur
    // verlopen; die terugsturen bij het opslaan zou dode links opleveren.
    imageRef: z.string().optional(),
    photoRefs: z.array(z.string()).optional(),
    linkUrl: z.string().url().optional(),
    authorId: DocId,
    authorName: z.string().max(100),
    publishedAt: IsoTimestamp,
    createdAt: IsoTimestamp,
    updatedAt: IsoTimestamp.optional(),
    viewCount: z.number().int().min(0).default(0),
    status: NieuwsPostStatus,
});
// `imageUrl` on input is an image *reference*: either a GCS object path from the
// photo-upload-url endpoint (stored privately, signed on read) OR a pasted
// external URL (passed through unchanged). The bucket is private — see
// image-url.service.resolveImageUrl. Hence max(2048) instead of .url().
//
// De prefix-eis is een veiligheidsgrens, geen vormcontrole: nieuws deelt de
// bucket met de profielfoto's (object `{userId}.jpg`) en resolveImageUrl
// ondertekent elk pad dat het krijgt. Zonder deze eis kan een medewerker
// `imageUrl: "collega.jpg"` posten en zo een leesbare URL op de privéfoto van
// een ander krijgen. `data:` staat toe omdat de dev-fallback zonder GCS-creds
// een data-URL opslaat. `..` wordt expliciet geweigerd zodat de grens niet
// leunt op hoe GCS een objectnaam als `nieuws/../collega.jpg` afhandelt.
const IMAGE_REF_RE = /^(https?:\/\/|data:image\/|nieuws\/)/;
const imageRef = z
    .string()
    .max(2048)
    .refine((v) => IMAGE_REF_RE.test(v) && !v.includes('..'), {
    message: 'Ongeldige afbeeldingsverwijzing',
});
const ImageRefInput = imageRef
    .optional()
    .or(z.literal(''))
    .transform((v) => v || undefined);
// 100 is geen technische grens maar een rem: één bericht is een teamuitje, geen
// mediabibliotheek. Elke foto kost een sign-call bij het openen van het bericht.
const PhotosInput = z.array(imageRef).max(100).optional();
const LinkUrlInput = z
    .string()
    .url()
    .optional()
    .or(z.literal(''))
    .transform((v) => v || undefined);
// `authorId` en `publishedAt` mogen door de client gezet worden zodat een
// beheerder een bericht op naam van een collega of met terugwerkende kracht kan
// plaatsen. `authorName` staat er bewust NIET bij: de route zoekt de naam op bij
// het account, anders kan iemand een willekeurige naam onder een bericht zetten.
export const CreateNieuwsPostInput = z.object({
    type: NieuwsPostType.default('post'),
    title: z.string().min(1).max(200).trim(),
    excerpt: z.string().max(500).trim().optional(),
    content: z.string().max(50000).trim().optional(),
    imageUrl: ImageRefInput,
    photos: PhotosInput,
    linkUrl: LinkUrlInput,
    authorId: DocId.optional(),
    publishedAt: IsoTimestamp.optional(),
    status: NieuwsPostStatus.default('published'),
});
export const UpdateNieuwsPostInput = z.object({
    type: NieuwsPostType.optional(),
    title: z.string().min(1).max(200).trim().optional(),
    excerpt: z.string().max(500).trim().optional(),
    content: z.string().max(50000).trim().optional(),
    imageUrl: ImageRefInput,
    photos: PhotosInput,
    linkUrl: LinkUrlInput,
    authorId: DocId.optional(),
    publishedAt: IsoTimestamp.optional(),
    status: NieuwsPostStatus.optional(),
});
//# sourceMappingURL=nieuws.js.map